import * as React from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';

// Renders a single data record (one row of many columns) as a responsive
// label/value field grid instead of a wide horizontal table. The grid reflows
// to the available width, so it never overflows or needs horizontal scrolling.
// Boolean-ish fields (0/1) render as Yes/No pills.

const useStyles = makeStyles((theme: Theme) => {
  const isDark = theme.palette.type === 'dark';
  const line = isDark ? 'rgba(255, 255, 255, 0.10)' : 'rgba(28, 30, 31, 0.10)';
  return {
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
      background: theme.palette.background.paper,
      border: `1px solid ${line}`,
      borderRadius: 12,
      overflow: 'hidden',
    },
    // Each cell draws its own right + bottom separator, so incomplete final
    // rows leave the card's own background (not a grey grid track) behind.
    cell: {
      padding: '10px 14px',
      boxShadow: `inset -1px -1px 0 0 ${line}`,
      minWidth: 0,
    },
    label: {
      fontSize: '0.7rem',
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      color: isDark ? '#F48FB6' : '#DA2972',
      fontWeight: 700,
      marginBottom: 4,
      lineHeight: 1.3,
    },
    value: {
      fontSize: '0.9rem',
      color: theme.palette.text.primary,
      wordBreak: 'break-word',
      lineHeight: 1.4,
    },
    muted: {
      color: theme.palette.text.disabled,
    },
    pill: {
      display: 'inline-block',
      fontSize: '0.72rem',
      fontWeight: 600,
      padding: '2px 10px',
      borderRadius: 999,
    },
    yes: {
      background: isDark ? 'rgba(29, 158, 117, 0.22)' : '#E1F5EE',
      color: isDark ? '#7FE3C4' : '#0F6E56',
    },
    no: {
      background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#F1EFE8',
      color: isDark ? '#B4B2A9' : '#5F5E5A',
    },
  };
});

// Fields whose 0/1 value should render as a Yes/No pill.
const BOOL_LABEL = /^(is |has |enable|paused|pay to|onboarding)|enabled|completed|active|blocked|verified|deleted|biometric|open banking/i;

const isEmpty = (v: any): boolean =>
  v === null || v === undefined || v === '' || v === 'null' || v === 'undefined';

const isTrue = (v: any): boolean => v === '1' || v === 1;

export const RecordFields = ({ labels, values }: { labels: any[]; values: any[] }) => {
  const classes = useStyles();
  return (
    <div className={classes.grid}>
      {labels.map((label: any, i: number) => {
        const raw = values ? values[i] : undefined;
        const labelText = String(label == null ? '' : label);
        const boolish = BOOL_LABEL.test(labelText) && (raw === '1' || raw === '0' || raw === 1 || raw === 0);
        return (
          <div key={i} className={classes.cell}>
            <div className={classes.label}>{labelText}</div>
            <div className={classes.value}>
              {isEmpty(raw) ? (
                <span className={classes.muted}>&mdash;</span>
              ) : boolish ? (
                <span className={`${classes.pill} ${isTrue(raw) ? classes.yes : classes.no}`}>
                  {isTrue(raw) ? 'Yes' : 'No'}
                </span>
              ) : (
                String(raw)
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RecordFields;
